import React, { useCallback, useEffect, useMemo } from 'react';
import { css, Theme, useTheme } from "@emotion/react";
import { Helmet } from "react-helmet-async";
import { isEmpty } from "lodash";
import { useBlocker, useNavigate } from "react-router-dom";

import { getSiteTitle } from "core/config/config";
import { useAppState } from "core/contexts/AppContext";
import { FieldList, OptionalOnFieldChangeArgs } from "core/hooks/useForm";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import useSetState from "core/hooks/useSetState";
import PageSection from "pages/Components/CartAndCheckout/components/PageSection";
import DetailsForm from "pages/Components/CartAndCheckout/components/DetailsForm";
import HeaderTitle from "pages/Components/CartAndCheckout/components/HeaderTitle";
import PageLeftSection from "pages/Components/CartAndCheckout/components/PageLeftSection";
import PageRightSection from "pages/Components/CartAndCheckout/components/PageRightSection";
import useCartDetailSizes from "pages/Components/CartAndCheckout/hooks/useCartDetailSizes";
import CheckoutPagination from "pages/Components/CartAndCheckout/components/CheckoutPagination";
import PaymentComponent from "pages/Components/CartAndCheckout/components/PaymentComponent";
import Billing from "pages/Components/CartAndCheckout/components/Billing";
import CartSummaryContainer from "pages/Components/CartAndCheckout/components/CartSummaryContainer";
import useDetailsForms, { BILLING_FIELDS_TO_USE, MANUAL_ADDRESS_FIELDS_FOR_SHIPPING } from "pages/Components/CartAndCheckout/hooks/useDetailsForms";
import usePlaceOrder from "pages/Components/CartAndCheckout/hooks/usePlaceOrder";
import CheckoutFinishDialog from './components/CheckoutFinishDialog';
import useCart from "pages/Components/CartPage/hooks/useCart";
import useShipping from "pages/Components/CartAndCheckout/hooks/useShipping";
import Loading from "core/Components/Utils/Loading";
import Authentication from "pages/Components/CartAndCheckout/components/Authentication";
import { hasFeatureFlag } from "core/includes/site";
import { FLAG_SHOP_ONLINE_VIDEOS } from "core/includes/featureFlags";
import { NOOP } from 'core/constants';
import window from "core/includes/window";
import useBreakpoint from "core/hooks/useBreakpoint";
import useToast from "core/hooks/useToast";
import { isUp, isDown } from "theme/breakpoints";

export type CheckoutStep = {
    label: string,
    component: React.ReactNode,
    headerTitle: string,
    key: string,
    isHidden?: boolean
    subHeaderTitle?: string,
}

type Props = {
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
}

type Stage = {
    step: number,
    key: string,
}

const STAGES: Stage[] = [
    {
        step: 0,
        key: 'login'
    },
    {
        step: 1,
        key: 'contact'
    },
    {
        step: 2,
        key: 'methods'
    },
    {
        step: 3,
        key: 'billing'
    },
];

type DefaultState = {
    checkoutStage: Stage,
    paymentOption: string,
    isGuest: boolean,
    isUsingShippingAddress: boolean,
    isSubmitting: boolean,
    orderNumber: string,
    isErrorDialogOpen: boolean,
    errorDialogMessage: string,
    shippingOption: any,
    suburbOptions?: string[],
    usedAddressFinder: boolean,
};

const INITIAL_STATE = {
    checkoutStage: STAGES[0],
    paymentOption: "Invoice",
    isUsingShippingAddress: true,
    isGuest: false,
    isSubmitting: false,
    orderNumber: "",
    isErrorDialogOpen: false,
    errorDialogMessage: "",
    shippingOption: {},
    suburbOptions: undefined,
    usedAddressFinder: false,
};

const DEFAULT_PAGE_TITLE = 'Checkout';

const CheckoutPage: React.FC<Props> = ({ showBreadcrumb, hideHeaderCountry }) => {
    const theme: Theme = useTheme();
    const [appState, setAppState] = useAppState();
    const [, errorToast] = useToast();
    const { breakpoint } = useBreakpoint();

    const hasOnlineEvent = useMemo(() => (
        appState.cart?.products?.some((product: CartProduct) => product.isDigital) || false
    ), [appState.cart?.products]);


    const [state, setState] = useSetState<DefaultState>({
        ...INITIAL_STATE,
        paymentOption: hasOnlineEvent ? "Credit card" : "Invoice"
    });

    useEffect(function scrollUpOnStageChange() {
        window.scrollTo({ top: 0, left: 0 });
    }, [state.checkoutStage.key]);

    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });
    const locationOrigin = window.location?.origin;
    const { paddingTop } = useCartDetailSizes();
    const { fields, errors, onFieldChange, validate } = useDetailsForms({
        validateBillingFields: state.checkoutStage.step === 2 && !state.isUsingShippingAddress,
    });
    const { getCountry } = useCart();
    const navigate: any = useNavigate();

    const countryCode = getCountry();

    const filteredFields = useMemo(() => {
        const contactFields: FieldList = {};
        const fieldsForBilling: FieldList = {};
        const billingFields: FieldList = {};
        const readOnlyFields: string[] = [];

        Object.keys(fields).forEach(key => {
            if (!key.includes('Billing')) {
                contactFields[key] = fields[key];
            }

            if (BILLING_FIELDS_TO_USE.includes(key)) {
                const billingKey = `${key}Billing`;
                billingFields[billingKey] = fields[billingKey];

                // Don't need address search input field for read only fields
                if (key === 'addressSearch') return;
                fieldsForBilling[key] = fields[key];
            }
        });

        if (countryCode.code.toLowerCase() === 'nz') {
            readOnlyFields.push('country');
        }

        return {
            fields: fieldsForBilling,
            billingFields,
            contactFields,
            readOnlyFields,
        }
    }, [countryCode.code, fields]);

    const { clearCartStorage, getLocalStorageCartUuid } = useCart();
    const { packageProducts, removeShipping, isShippingLoading } = useShipping({
        filteredFields,
        usedAddressFinder: state.usedAddressFinder
    });

    useBlocker(() => {
        if (getLocalStorageCartUuid()) {
            removeShipping();
            setState({ shippingOption: {} });
            setAppState((prevState: any) => ({
                cart: {
                    ...prevState.cart,
                    shippingAmount: 0,
                }
            }));
        }
        return false;
    });

    const {
        paymentSubmit,
        sendInvoice,
        state: orderState,
        setState: setOrderState,
    } = usePlaceOrder({
        fields: state.isUsingShippingAddress ? filteredFields.fields : filteredFields.billingFields,
        detailFields: filteredFields.contactFields,
    });

    const onAddressFinder = useCallback((isUsed = true) => {
        setState({ usedAddressFinder: isUsed })
    }, [setState])

    const _onFieldChange = useCallback((
        name: string,
        value: any,
        otherProps?: OptionalOnFieldChangeArgs
    ) => {
        onFieldChange(name, value, otherProps)

        if (state.usedAddressFinder && MANUAL_ADDRESS_FIELDS_FOR_SHIPPING.includes(name)) {
            onAddressFinder(false)
        }
    }, [onAddressFinder, onFieldChange, state.usedAddressFinder])

    const onPaymentOptionSelect = useCallback((option: string) => setState({ paymentOption: option }), [setState]);
    const onLoginOptionSelect = useCallback((isGuest: boolean) => {
        setState({ isGuest, ...isGuest && { checkoutStage: STAGES[1] } });
    }, [setState]);

    const onAddressChange = useCallback((usingShippingAddress = false) => {
        if (usingShippingAddress !== state.isUsingShippingAddress) {
            setState({ isUsingShippingAddress: usingShippingAddress });
        }
    }, [setState, state.isUsingShippingAddress]);

    // Update the field 'city' to the suggested suburb name.
    const setSuggestedSuburb = useCallback((suburbName: string) => {
        onFieldChange('city', suburbName);
    }, [onFieldChange]);

    // Remove the suggested suburb name.
    const clearSuburbOptions = useCallback(() => {
        setState({ suburbOptions: undefined });
    }, [setState]);

    const isInvoice = state.paymentOption === 'Invoice';
    const hasShopOnlineVideosFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_ONLINE_VIDEOS);

    const paginationSteps: CheckoutStep[] = useMemo(() => [
        {
            label: 'Login',
            component: <Authentication
                onSelect={onLoginOptionSelect}
            />,
            headerTitle: 'Authentication',
            key: 'login',
            isHidden: !hasShopOnlineVideosFeatureFlag || !!appState.user,
        },
        {
            label: 'Detail',
            component: <DetailsForm
                fields={filteredFields.contactFields}
                readOnlyFields={filteredFields.readOnlyFields}
                errors={errors}
                onFieldChange={_onFieldChange}
                suburbOptions={state.suburbOptions}
                setSuggestedSuburb={setSuggestedSuburb}
                clearSuburbOptions={clearSuburbOptions}
                onAddressFinder={onAddressFinder}
                isLoading={isShippingLoading}
                hasAsterisk
            />,
            headerTitle: 'Your details',
            key: 'contact',
        },
        {
            label: 'Payment method',
            component: <PaymentComponent onSelect={onPaymentOptionSelect} selectedOption={state.paymentOption} />,
            headerTitle: 'Choose your payment method',
            key: 'methods',
            isHidden: hasShopOnlineVideosFeatureFlag && hasOnlineEvent,
        },
        {
            label: 'Billing',
            component: <Billing
                fields={filteredFields}
                isInvoice={isInvoice}
                onFieldChange={_onFieldChange}
                errors={errors}
                orderState={orderState}
                setOrderState={setOrderState}
                isUsingShippingAddress={state.isUsingShippingAddress}
                onAddressChange={onAddressChange}
                onAddressFinder={onAddressFinder}
            />,
            headerTitle: isInvoice ? 'Send invoice' : 'Payment',
            key: 'billing',
            ...isInvoice && {
                subHeaderTitle: ' to your email',
            },
        }
    ], [
        hasShopOnlineVideosFeatureFlag,
        appState.user,
        onLoginOptionSelect,
        filteredFields,
        errors,
        _onFieldChange,
        state.suburbOptions,
        state.paymentOption,
        state.isUsingShippingAddress,
        setSuggestedSuburb,
        clearSuburbOptions,
        onAddressFinder,
        hasOnlineEvent,
        onPaymentOptionSelect,
        isInvoice,
        orderState,
        setOrderState,
        onAddressChange,
        isShippingLoading
    ]);

    useEffect(() => {
        // Progress the checkout stage to the contact details' page if a user is authorised
        if (((appState.authorised || !hasShopOnlineVideosFeatureFlag) && state.checkoutStage.key === 'login')) {
            setState({ checkoutStage: STAGES[1] });

            // if a user has an online event in their cart, not authorised and not on the login page, then move them back
        } else if (hasShopOnlineVideosFeatureFlag &&
            hasOnlineEvent &&
            isEmpty(appState.user) &&
            state.checkoutStage.key !== 'login'
        ) {
            setState({ checkoutStage: STAGES[0] });
        }
    }, [
        appState.authorised,
        appState.user,
        hasOnlineEvent,
        hasShopOnlineVideosFeatureFlag,
        setState,
        state.checkoutStage.key,
        state.checkoutStage.step
    ]);

    const validateFields = useCallback(() => {
        if (!['contact', 'billing'].includes(state.checkoutStage.key)) return true;
        return validate();
    }, [state.checkoutStage, validate]);

    const onNextStep = useCallback(async () => {
        if (!validateFields()) return;

        const hasProduct = !!appState.cart?.products?.length;
        const hasPhysicalProducts = !!appState.cart?.products?.filter((product: CartProduct) => product.type !== 'event').length;

        let shippingPackage: any = {};
        let hasShippingOptions = false;

        if (state.checkoutStage.key === 'contact' && hasProduct && hasPhysicalProducts) {
            await packageProducts().then((res) => {
                shippingPackage = res;
                if (shippingPackage && shippingPackage.shippingOptions?.shippingCostExcludingGST > 0) {
                    hasShippingOptions = Object.keys(shippingPackage.shippingOptions).length > 0;
                    setState({
                        ...hasShippingOptions ? {
                            shippingOption: shippingPackage.shippingOptions
                        } : {
                            suburbOptions: shippingPackage.suburbOptions
                        }
                    });
                } else {
                    // Shipping cost cannot be null or 0.
                    setState({
                        isErrorDialogOpen: true,
                        errorDialogMessage:
                            `
                                We could not calculate shipping for this address. Please check your address or contact
                                us for help on <a href="mailto:comms@wtbox.com">comms@wtbox.com</a>.
                            `,
                    });
                }
            }).catch((err) => {
                errorToast(err.message);
            });
        }

        setState((prev: DefaultState) => {
            const hasShipping = (prev.checkoutStage.key === 'contact' && hasShippingOptions) ||
                (prev.checkoutStage.step > 1 && (hasShippingOptions || !isEmpty(prev.shippingOption)));

            const nextStageIndex = getNextPageIndex(
                Math.min(prev.checkoutStage.step + 1, paginationSteps.length - 1),
                paginationSteps,
                true
            );
            return {
                ...prev,
                isSubmitting: false,
                ...((!hasProduct || hasShipping || !hasPhysicalProducts) && prev.checkoutStage.step < paginationSteps.length - 1) && {
                    checkoutStage: STAGES[nextStageIndex]
                }
            };
        })
    }, [validateFields, appState.cart?.products, state.checkoutStage.key, setState, packageProducts, paginationSteps]);

    const onBackStep = useCallback(() => {
        // Current checkoutStage is 0 and back button was clicked: Move to the Cart page
        if (state.checkoutStage.step === 0 ||
            (state.checkoutStage.key === 'contact' && (appState.authorised || !hasShopOnlineVideosFeatureFlag))
        ) {
            setAppState((prevState: any) => ({
                cart: {
                    ...prevState.cart,
                    shippingAmount: 0
                }
            }));
            navigate("/cart-summary");
        } else {
            setState((prevState: DefaultState) => {
                const nextStageIndex = getNextPageIndex(Math.max(prevState.checkoutStage.step - 1, 0), paginationSteps, false);
                return {
                    checkoutStage: STAGES[nextStageIndex]
                }
            });
        }
    }, [
        state.checkoutStage.step,
        state.checkoutStage.key,
        appState.authorised,
        hasShopOnlineVideosFeatureFlag,
        setAppState,
        navigate,
        setState,
        paginationSteps
    ]);

    const onStepClick = useCallback((step: number) => setState({ checkoutStage: STAGES[step] }), [setState]);

    const currentStep = paginationSteps.find((step) => step.key === state.checkoutStage.key);
    const currentStepIndex = paginationSteps.findIndex((step) => step.key === state.checkoutStage.key)
    const component = currentStep?.component;

    const onClearCart = useCallback(() => {
        clearCartStorage()
    }, [clearCartStorage]);

    const onPlaceOrder = useCallback(() => {
        setState({ isSubmitting: true });
        // Check the available quantity for the products on the cart
        if (!validateFields()) {
            setState({ isSubmitting: false });
            return;
        }

        // It should call the onNextStep function, not the mutation for sendInvoice or paymentSubmit
        // , when the "Try again"  button is clicked on the shipping cost error pop-up.
        if (state.checkoutStage.key === "contact") {
            onNextStep().catch(() => {});
            return;
        }

        // Return array definition
        // sendInvoice => 0: createDraftOrderResponse, 1: sendOrderInvoiceResponse
        // paymentSubmit => 0: paymentResponse, 1: createDraftOrderResponse, 2: completeOrderResponse
        const mutation = isInvoice ? sendInvoice : paymentSubmit;
        const responseDataIndex = isInvoice ? 1 : 2;
        let isErrorDialogOpen = true;

        mutation()
            .then((response: any) => {
                if (response === 'card-error') {
                    isErrorDialogOpen = false;
                    throw new Error('Card error.');
                }

                const responseData = response[responseDataIndex];
                const data = isInvoice ? responseData?.data?.sendOrderInvoice : responseData?.data?.completeOrder;

                setState({
                    orderNumber: data || "",
                    isSubmitting: false,
                    isErrorDialogOpen: !data
                });
            })
            .catch((error) => {
                console.error(error);
                setState({
                    isErrorDialogOpen,
                    isSubmitting: false,
                });
            });
    }, [setState, validateFields, isInvoice, sendInvoice, paymentSubmit]);

    const isLast = currentStepIndex === paginationSteps.length - 1;

    const isDialogOpen = state.isErrorDialogOpen || !!state.orderNumber;
    const isErrorDialog = isDialogOpen && (state.isErrorDialogOpen);
    const isInvoiceDialog = isDialogOpen && (isInvoice && !isErrorDialog);
    const isPaymentDialog = isDialogOpen && (!isInvoice && !isErrorDialog);
    const isSuccessDialog = isDialogOpen && (isInvoiceDialog || isPaymentDialog);
    const isPrevStepHidden = currentStepIndex > 0 && paginationSteps[currentStepIndex - 1].isHidden;
    const backIsToCart = isPrevStepHidden ?
        paginationSteps[currentStepIndex - 1] === paginationSteps[0] :
        paginationSteps[currentStepIndex] === paginationSteps[0];
    const stepBackButtonText = backIsToCart ? "Back to cart" : "Back";
    const showNextButton = currentStepIndex !== paginationSteps.length - 1 &&
        state.checkoutStage.key !== 'login';

    const bottomSectionMessageTwo = useMemo(() => {
        switch (true) {
            case (isErrorDialog):
                return 'Your payment cannot be completed.';
            case (isInvoiceDialog):
                return 'A copy of your invoice and order number has been sent to your email.';
            case (isPaymentDialog):
                return 'Your order details have been sent to your email.';
            default:
                return '';
        }
    }, [isErrorDialog, isInvoiceDialog, isPaymentDialog]);

    // Set the breadcrumb label
    useEffect(() => {
        setAppState({
            currentPageName: DEFAULT_PAGE_TITLE,
            loading: false,
        });
    }, [setAppState]);

    // Reset error if payment method changes
    useEffect(() => {
        setOrderState({
            errorMessage: '',
            isVisibleErrorMessage: false,
        });
    }, [isInvoice, setOrderState]);

    useEffect(() => {
        if (!state.isSubmitting &&
            !isDialogOpen &&
            !state.orderNumber &&
            !appState.isCartLoading &&
            !getLocalStorageCartUuid() &&
            !appState.cart
        ) {
            navigate('/');
        }
    }, [
        appState.cart,
        navigate,
        getLocalStorageCartUuid,
        appState.isCartLoading,
        state.isSubmitting,
        state.orderNumber,
        isDialogOpen
    ]);

    const leftSectionStyle = css`
        flex-direction: column;
        justify-content: space-between;

        ${theme.breakpoints.up('3xl')} {
            padding: ${paddingTop}px 0 37px 0;
        }

        ${theme.breakpoints.only('xxl')} {
            padding: ${paddingTop}px 0 58px 0;
        }

        ${theme.breakpoints.only('xl')} {
            padding: ${paddingTop}px 0 48px 0;
        }

        ${theme.breakpoints.only('lg')} {
            padding: ${paddingTop}px 0 35px 0;
        }

        ${theme.breakpoints.only('md')} {
            padding: ${paddingTop}px 0 26px 0;
        }

        ${theme.breakpoints.only('sm')} {
            padding: ${paddingTop}px 0 27px 0;
        }

        ${theme.breakpoints.down('xs')} {
            padding: ${paddingTop}px 11px 34px 12px;
        }
    `;

    const wrapperStyle = css`
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        margin-left: auto;

        ${theme.breakpoints.up('3xl')} {
            margin-right: 195px;
        }

        ${theme.breakpoints.up('xl')} {
            max-width: 618px;
        }

        ${theme.breakpoints.only('xxl')} {
            margin-right: 150px;
        }

        ${theme.breakpoints.only('xl')} {
            margin-right: 56px;
        }

        ${theme.breakpoints.only('lg')} {
            max-width: 581px;
            margin-right: 38px;
        }

        ${theme.breakpoints.only('lg')} {
            margin-right: 37px;
        }

        ${theme.breakpoints.only('md')} {
            max-width: 604px;
            margin-right: auto;
        }

        ${theme.breakpoints.only('sm')} {
            max-width: 494px;
            margin-right: auto;
        }

        ${theme.breakpoints.down('xs')} {
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
        }
    `;

    const headerStyle = css`
        justify-content: start;
        padding-bottom: 19px;
        font-weight: ${theme.fonts.weights.normal};
    `;

    const pageRightSectionStyle = css`
        height: 100%;
    `;

    const messageTwoStyle = css`
        .bottom-section-messages {
            width: 300px;

            div {
                width: 100%;
            }
        }
    `;

    const buttonStyle = css`
        ${theme.mixins.button.types.nice.base};
        
        width: 203px;
        height: 47px;
        font-size: 22px;
        font-weight: ${theme.fonts.weights.bold};
        cursor: pointer;
        box-shadow: ${theme.borderAndShadow.boxShadow};
    `;

    const stepNextButtonStyle = css`
        ${buttonStyle};
        --button-bg-colour: white;
    `;

    const stepBackButtonStyle = css`
        ${buttonStyle};
        --button-bg-colour: ${theme.colours.curiousBlue2};
        
        color: white;
        border: 1px solid white;
    `;

    const stepChangeButtonsContainerStyle = css`
        padding-top: 40px;
        display: flex;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        gap: 20px;
        
        ${theme.breakpoints.down("md")} {
            padding-top: 0;
            padding-bottom: 40px;
        }
    `;

    return (
        <>
            <Helmet>
                {locationOrigin && (
                    <link rel="canonical" href={locationOrigin} />
                )}
                <title>{getSiteTitle("Checkout")}</title>
            </Helmet>
            <PageSection headerHeight={headerHeight}>
                {(appState.isCartLoading || (getLocalStorageCartUuid() && !appState.cart)) ? (
                    <Loading overlayColour={"white"} delay={500} onTop fitToScreen overlay />
                ) : (
                    <>
                        {isDialogOpen && (
                            <CheckoutFinishDialog
                                heading={isErrorDialog ? 'Oh No!' : 'Thank You!'}
                                buttonText={isErrorDialog ? 'Try again' : 'Continue shopping'}
                                bottomSectionMessageTwo={state.errorDialogMessage || bottomSectionMessageTwo}
                                {...isSuccessDialog && {
                                    bottomSectionMessageOne: `Order Number: #${state.orderNumber}`,
                                    closeTo: '/',
                                    onClose: () => onClearCart(),
                                    onClickButton: () => onClearCart(),
                                }}
                                {...isInvoiceDialog && {
                                    css: messageTwoStyle,
                                    bottomColour: theme.colours.malibu5,
                                    circleIcon: 'email',
                                }}
                                {...isPaymentDialog && {
                                    subheading: 'Your payment was successful.',
                                }}
                                {...isErrorDialog && {
                                    subheading: `Something's gone wrong.`,
                                    css: messageTwoStyle,
                                    bottomColour: theme.colours.red1,
                                    circleIcon: 'exclamation',
                                    buttonTo: '',
                                    onClose: () => {
                                        setState({ isErrorDialogOpen: false })
                                    },
                                    onClickButton: () => {
                                        setState({ isErrorDialogOpen: false });
                                        onPlaceOrder();
                                    }
                                }}
                            />
                        )}
                        <PageLeftSection css={leftSectionStyle}>
                            <div css={wrapperStyle}>
                                {isDown(breakpoint, "md") && (
                                    <CheckoutPagination
                                        steps={paginationSteps}
                                        currentStep={currentStepIndex}
                                        onNext={state.checkoutStage.key === 'login' ? NOOP : onNextStep}
                                        onBack={onBackStep}
                                        onStepClick={onStepClick}
                                    />
                                )}
                                <HeaderTitle textColour={'black'} css={headerStyle}>
                                    {currentStep?.headerTitle}&nbsp;
                                    {currentStep?.subHeaderTitle}
                                </HeaderTitle>
                                {component}
                                {isUp(breakpoint, "lg") && (
                                    <CheckoutPagination
                                        steps={paginationSteps}
                                        currentStep={currentStepIndex}
                                        onNext={state.checkoutStage.key === 'login' ? NOOP : onNextStep}
                                        onBack={onBackStep}
                                        onStepClick={onStepClick}
                                    />
                                )}
                            </div>
                        </PageLeftSection>
                        <PageRightSection paddingTop={paddingTop} css={pageRightSectionStyle}>
                            {isDown(breakpoint, "md") && (
                                <div css={stepChangeButtonsContainerStyle}>
                                    {showNextButton && (
                                        <button
                                            css={stepNextButtonStyle}
                                            onClick={state.checkoutStage.key === 'login' ? NOOP : onNextStep}
                                        >
                                            Next
                                        </button>
                                    )}
                                    {isLast && !isSuccessDialog && (
                                        <button
                                            css={stepNextButtonStyle}
                                            onClick={onPlaceOrder}
                                            disabled={state.isSubmitting}
                                        >
                                            {state.paymentOption === 'Invoice' ? 'Send invoice' : 'Place order'}
                                        </button>
                                    )}
                                    <button
                                        onClick={onBackStep}
                                        css={stepBackButtonStyle}
                                    >
                                        {stepBackButtonText}
                                    </button>
                                </div>
                            )}
                            <CartSummaryContainer isCheckout>
                                {isUp(breakpoint, "lg") && (
                                    <div css={stepChangeButtonsContainerStyle}>
                                        {showNextButton && (
                                            <button
                                                css={stepNextButtonStyle}
                                                onClick={state.checkoutStage.key === 'login' ? NOOP : onNextStep}
                                            >
                                                Next
                                            </button>
                                        )}
                                        {isLast && !isSuccessDialog && (
                                            <button
                                                css={stepNextButtonStyle}
                                                onClick={onPlaceOrder}
                                                disabled={state.isSubmitting}
                                            >
                                                {state.paymentOption === 'Invoice' ? 'Send invoice' : 'Place order'}
                                            </button>
                                        )}
                                        <button
                                            onClick={onBackStep}
                                            css={stepBackButtonStyle}
                                        >
                                            {stepBackButtonText}
                                        </button>
                                    </div>
                                )}
                            </CartSummaryContainer>
                        </PageRightSection>
                    </>
                )}
            </PageSection>
        </>
    )
};

export default CheckoutPage;

const getNextPageIndex = (currentIndex: number, steps: CheckoutStep[], isIncrementing: boolean,) => {
    let newIndex = Math.max(currentIndex, 0);

    while (newIndex >= 0 && newIndex <= steps.length - 1 && steps[newIndex].isHidden) {
        if (isIncrementing) {
            newIndex++;
        } else {
            newIndex--;
        }
    }

    return newIndex;
}